import { styled, css, keyframes } from "styled-components";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulsate = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

// Shared styles for form elements
const formInputStyles = css`
  width: 100%;
  padding: 12px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 1em;
  color: #495057;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  }
`;

// Main Containers
export const RenewalPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  background-color: #f0f2f5; // A softer gray background
  min-height: calc(100vh - 120px);
  font-family: 'Poppins', sans-serif;
`;

export const ContentBox = styled.div`
  background: #fff;
  padding: 40px;
  border-radius: 16px; // Slightly larger border-radius
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1); // More pronounced shadow
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 30px; // Increased gap for more spacing
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;
`;

// Typography
export const Title = styled.h2`
  color: #007bff;
  font-size: 2.5em;
  font-weight: 700;
  margin-bottom: 5px;
  letter-spacing: -0.5px;
`;

export const Subtitle = styled.h3`
  color: #495057;
  font-size: 1.5em;
  font-weight: 600;
  margin-top: 0;
`;

// Image and Info Sections
export const BusinessImage = styled.img`
  width: 100%;
  max-height: 280px;
  object-fit: cover; // Changed to cover for a better fit
  border-radius: 12px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
`;

export const StallInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); // Responsive grid
  gap: 20px;
  margin-bottom: 30px;
  text-align: left;
`;

export const InfoItem = styled.div`
  background-color: #f1f5f9; // Lighter, more modern background color
  padding: 20px;
  border-radius: 10px;
  font-size: 1em;
  color: #495057;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  }

  strong {
    color: #212529;
    font-size: 0.9em;
    margin-bottom: 5px;
    font-weight: 700;
    text-transform: uppercase;
  }
`;

export const StatusMessage = styled.p<{isActive: boolean }>`
  font-size: 1.5em;
  font-weight: bold;
  padding: 15px;
  border-radius: 8px;
  color: #fff;
  background-color: ${props => props.isActive ? '#28a745' : '#dc3545'};
  margin-top: 10px;
  margin-bottom: 20px;
  animation: ${pulsate} 2s infinite ease-in-out;
`;

// Payment and Form Sections
export const FormSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 25px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 20px;
  background-color: #f9fafb;
`;

export const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  color: #007bff;
  font-size: 1.8em;
  margin-bottom: 15px;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;

  h3 {
    margin: 0;
    font-weight: 600;
  }
`;

export const FormRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

interface FormGroupProps {
  error?: boolean;
}

export const FormGroup = styled.div<FormGroupProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 15px;
  border: ${props => props.error ? '1px solid #dc3545' : 'none'};
  padding: ${props => props.error ? '10px' : '0'};
  border-radius: ${props => props.error ? '8px' : '0'};
  animation: ${props => props.error && css`${fadeIn} 0.5s ease-in`};
`;

export const Label = styled.label`
  font-size: 1.1em;
  color: #343a40;
  font-weight: bold;
  margin-bottom: 8px;
`;

export const RequiredIndicator = styled.span`
  color: #dc3545;
  margin-left: 5px;
`;

export const RadioContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

export const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1em;
  cursor: pointer;
  color: #495057;
  font-weight: 500;

  input {
    ${formInputStyles}
    width: 20px;
    height: 20px;
    border-radius: 50%;
    
    &:checked {
      background-color: #007bff;
      border-color: #007bff;
    }
  }
`;

export const PaymentOptionsContainer = styled.div`
  margin-top: 30px;
  border-top: 2px solid #dee2e6;
  padding-top: 30px;
`;

export const PaymentHeader = styled.h4`
  color: #007bff;
  font-size: 1.8em;
  font-weight: 600;
  margin-bottom: 25px;
  letter-spacing: -0.5px;
`;

export const PaymentButtonsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
`;

export const PaymentButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 25px 20px;
  background-color: #f1f5f9;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 1.2em;
  font-weight: bold;
  color: #495057;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background-color: #e2e6ea;
    border-color: #007bff;
    box-shadow: 0 6px 15px rgba(0, 123, 255, 0.2);
    color: #007bff;
    transform: translateY(-5px);
  }

  &:disabled {
    cursor: not-allowed;
    background-color: #e9ecef;
    color: #6c757d;
    opacity: 0.8;
  }

  svg {
    font-size: 3em;
    margin-bottom: 15px;
    color: #6c757d;
    transition: color 0.3s ease;
  }

  &:hover:not(:disabled) svg {
    color: #007bff;
  }
`;

export const PaymentDisclaimer = styled.p`
  font-size: 0.95em;
  color: #555;
  margin-top: 30px;
  line-height: 1.6;
  background-color: #ffeeba;
  border-left: 5px solid #ffc107;
  padding: 20px;
  border-radius: 8px;
  text-align: left;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

export const RadioInput = styled.input`
cursor: pointer;
`;